import { MODULES } from '@ibe/constants';
import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect, LoadingState } from '@ibe/ui';
import { type UseQueryResult } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form';

import { useAuth } from '../../../auth/auth-context.js';
import { EntitySelect } from '../../../components/form/entity-select.js';
import { PropertySelect } from '../../../components/form/property-select.js';
import { useApiList } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { useCurrencies, useProperty } from '../../../modules/properties/api/queries.js';
import { roomTypeRow } from '../../../modules/room-types/api/schemas.js';
import {
  RATE_PLAN_MEAL_OPTIONS,
  RATE_PLAN_STATUS_OPTIONS,
  RATE_PLAN_TYPE_OPTIONS,
  type RatePlanFormInput,
} from '../api/schemas.js';

export interface RatePlanFieldsProps {
  readonly register: UseFormRegister<RatePlanFormInput>;
  readonly errors: FieldErrors<RatePlanFormInput>;
  readonly control: Control<RatePlanFormInput>;
  readonly mode: 'create' | 'edit';
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

function CancellationPolicySelect({
  propertyId,
  value,
  onChange,
  error,
}: {
  readonly propertyId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string;
}): React.JSX.Element {
  return (
    <EntitySelect
      path="/v1/cancellation-policies/options"
      queryKey={queryKeys.cancellationPolicies.options(propertyId)}
      params={propertyId === '' ? undefined : { propertyId }}
      value={value}
      onChange={onChange}
      label="Cancellation policy"
      error={error}
      placeholder="Choose a cancellation policy"
      enabled={propertyId !== ''}
      disabledHint="Choose a property first"
    />
  );
}

function PaymentPolicySelect({
  propertyId,
  value,
  onChange,
  error,
}: {
  readonly propertyId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string;
}): React.JSX.Element {
  return (
    <EntitySelect
      path="/v1/payment-policies/options"
      queryKey={queryKeys.paymentPolicies.options(propertyId)}
      params={propertyId === '' ? undefined : { propertyId }}
      value={value}
      onChange={onChange}
      label="Payment policy"
      error={error}
      placeholder="Choose a payment policy"
      enabled={propertyId !== ''}
      disabledHint="Choose a property first"
    />
  );
}

const roomTypeListResponse = roomTypeRow;

function RoomTypeSelect({
  propertyId,
  value,
  onChange,
  error,
}: {
  readonly propertyId: string;
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  readonly error?: string;
}): React.JSX.Element {
  const query = useApiList({
    resource: 'roomTypes',
    path: '/v1/room-types',
    schema: roomTypeListResponse,
    params: { page: 1, pageSize: 100, scope: { propertyId } },
    enabled: propertyId !== '',
  });

  return (
    <Field error={error}>
      <FieldLabel>Room types</FieldLabel>
      {propertyId === '' ? (
        <p className="text-caption text-muted-foreground">Choose a property first.</p>
      ) : query.isPending ? (
        <LoadingState label="Loading room types" />
      ) : query.error !== null ? (
        <p className="text-caption text-destructive">Could not load room types.</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {query.data.items.map((roomType) => {
            const selected = value.includes(roomType.id);
            return (
              <FieldCheckbox
                key={roomType.id}
                checked={selected}
                onCheckedChange={(checked) => {
                  onChange(
                    checked ? [...value, roomType.id] : value.filter((id) => id !== roomType.id),
                  );
                }}
              >
                {roomType.name ?? roomType.code ?? roomType.id}
              </FieldCheckbox>
            );
          })}
        </div>
      )}
    </Field>
  );
}

function CurrencySelect({
  value,
  onChange,
  error,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string;
}): React.JSX.Element {
  const query: UseQueryResult<readonly { code: string; name: string }[]> = useCurrencies();
  const options = (query.data ?? []).map((currency) => ({
    value: currency.code,
    label: `${currency.code} — ${currency.name}`,
  }));

  return (
    <Field error={error} required>
      <FieldLabel>Currency</FieldLabel>
      <FieldSelect
        value={value}
        onValueChange={onChange}
        options={options}
        placeholder="Choose a currency"
        searchable
      />
    </Field>
  );
}

export function RatePlanFields({
  register,
  errors,
  control,
  mode,
}: RatePlanFieldsProps): React.JSX.Element {
  const auth = useAuth();
  const propertyId = useWatch<RatePlanFormInput, 'propertyId'>({ control, name: 'propertyId' });
  const propertyQuery = useProperty(propertyId);
  const pmsConfigured = propertyQuery.data?.pmsConfigured ?? false;
  const showPmsField = auth.hasModule(MODULES.PMS) && pmsConfigured;

  return (
    <div className="space-y-6">
      <Section
        title="Identity"
        description="Which property this plan belongs to, and how it is named and coded in channel-manager mappings."
      >
        <Controller
          control={control}
          name="propertyId"
          render={({ field }) => (
            <PropertySelect
              value={field.value}
              onChange={field.onChange}
              error={errors.propertyId?.message}
              required
              readOnly={mode === 'edit'}
            />
          )}
        />

        <Row>
          <Field error={errors.code?.message} required>
            <FieldLabel>Code</FieldLabel>
            <FieldInput autoComplete="off" {...register('code')} />
          </Field>

          <Field error={errors.name?.message} required>
            <FieldLabel>Name</FieldLabel>
            <FieldInput autoComplete="off" {...register('name')} />
          </Field>
        </Row>

        <Row>
          <Field error={errors.type?.message ?? ''}>
            <FieldLabel>Type</FieldLabel>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={RATE_PLAN_TYPE_OPTIONS}
                  placeholder="Choose a rate plan type"
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Field error={errors.description?.message}>
          <FieldLabel>Description</FieldLabel>
          <FieldInput autoComplete="off" {...register('description')} />
        </Field>
      </Section>

      <Section
        title="Pricing rules"
        description="Currency, meal plan and the room types this rate applies to."
      >
        <Row>
          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <CurrencySelect
                value={field.value}
                onChange={field.onChange}
                error={errors.currency?.message ?? ''}
              />
            )}
          />

          <Field error={errors.mealPlan?.message}>
            <FieldLabel>Meal plan</FieldLabel>
            <Controller
              control={control}
              name="mealPlan"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={RATE_PLAN_MEAL_OPTIONS}
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Controller
          control={control}
          name="roomTypeIds"
          render={({ field }) => (
            <RoomTypeSelect
              propertyId={propertyId}
              value={field.value}
              onChange={field.onChange}
              error={errors.roomTypeIds?.message ?? ''}
            />
          )}
        />

        <Field error={errors.refundable?.message}>
          <Controller
            control={control}
            name="refundable"
            render={({ field }) => (
              <FieldCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
              >
                Refundable
              </FieldCheckbox>
            )}
          />
        </Field>
      </Section>

      <Section
        title="Stay and booking windows"
        description="Limits on how long a stay must be, how far ahead it must be booked, and when the plan is active."
      >
        <Row>
          <Field error={errors.minLengthOfStay?.message}>
            <FieldLabel>Minimum length of stay</FieldLabel>
            <FieldInput inputMode="numeric" autoComplete="off" {...register('minLengthOfStay')} />
          </Field>

          <Field error={errors.maxLengthOfStay?.message}>
            <FieldLabel>Maximum length of stay</FieldLabel>
            <FieldInput inputMode="numeric" autoComplete="off" {...register('maxLengthOfStay')} />
          </Field>
        </Row>

        <Row>
          <Field error={errors.advanceBookingDays?.message}>
            <FieldLabel>Advance booking days</FieldLabel>
            <FieldInput
              inputMode="numeric"
              autoComplete="off"
              {...register('advanceBookingDays')}
            />
          </Field>

          <Field error={errors.status?.message}>
            <FieldLabel>Status</FieldLabel>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={RATE_PLAN_STATUS_OPTIONS}
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Row>
          <Field error={errors.activeFrom?.message}>
            <FieldLabel>Active from</FieldLabel>
            <FieldInput type="date" autoComplete="off" {...register('activeFrom')} />
          </Field>

          <Field error={errors.activeTo?.message}>
            <FieldLabel>Active to</FieldLabel>
            <FieldInput type="date" autoComplete="off" {...register('activeTo')} />
          </Field>
        </Row>
      </Section>

      <Section
        title="Policies"
        description="The cancellation and payment terms that apply to every booking under this plan."
      >
        <Controller
          control={control}
          name="cancellationPolicyId"
          render={({ field }) => (
            <CancellationPolicySelect
              propertyId={propertyId}
              value={field.value}
              onChange={field.onChange}
              error={errors.cancellationPolicyId?.message ?? ''}
            />
          )}
        />

        <Controller
          control={control}
          name="paymentPolicyId"
          render={({ field }) => (
            <PaymentPolicySelect
              propertyId={propertyId}
              value={field.value}
              onChange={field.onChange}
              error={errors.paymentPolicyId?.message ?? ''}
            />
          )}
        />
      </Section>

      {showPmsField ? (
        <Section
          title="PMS mapping"
          description="The identifier this rate plan maps to in the property's PMS."
        >
          <Field error={errors.pmsRateId?.message}>
            <FieldLabel>PMS rate id</FieldLabel>
            <FieldInput autoComplete="off" {...register('pmsRateId')} />
          </Field>
        </Section>
      ) : null}
    </div>
  );
}
